/**
 * Redux slice for external PricingSaaS API data
 * Handles caching, request tracking, and cost monitoring
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Diff } from '@/utils/externalDiffsTransformer';

// Cache TTL: 15 minutes (longer than local due to API cost)
const CACHE_TTL = 15 * 60 * 1000;

interface Statistics {
  totalDiffs: number;
  uniqueCompanies: number;
  totalChanges: number;
  eventTypeCounts: Record<string, number>;
}

interface CachedData<T> {
  data: T;
  lastFetched: number;
  periodFilter: string;
  slug?: string;
  event_types?: string[];
  published?: boolean;
  qa_status?: 'checked' | 'pending';
  sort_by?: 'created_at' | 'qa_check';
  sort_order?: 'asc' | 'desc';
}

interface RequestLog {
  timestamp: number;
  endpoint: string;
  params: Record<string, any>;
}

interface ExternalDiffsState {
  // Lists tab data
  listCache: CachedData<{
    diffs: Diff[];
    totalCount: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  }> | null;

  listStatistics: CachedData<Statistics> | null;

  // Company logos cache (to avoid repeated Supabase lookups)
  companyLogos: Record<string, string>; // slug -> logo URL

  // Request tracking for deduplication (array instead of Set for Redux serialization)
  requestsInFlight: string[];

  // Cost tracking
  apiRequestCount: number;
  requestLog: RequestLog[];

  // Navigation list for lightbox
  navigationList: string[] | null;

  // Current filters
  currentFilters: {
    slug?: string;
    period?: string;
    event_types?: string[];
    status_filter?: 'all' | 'published' | 'qa_checked' | 'qa_pending';
    sort_by?: 'created_at' | 'qa_check';
    sort_order?: 'asc' | 'desc';
  };
}

const initialState: ExternalDiffsState = {
  listCache: null,
  listStatistics: null,
  companyLogos: {},
  requestsInFlight: [],
  apiRequestCount: 0,
  requestLog: [],
  navigationList: null,
  currentFilters: {},
};

const externalDiffsSlice = createSlice({
  name: 'externalDiffs',
  initialState,
  reducers: {
    // Set diffs list data
    setExternalDiffsList: (state, action: PayloadAction<{
      diffs: Diff[];
      totalCount: number;
      currentPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
      periodFilter: string;
      slug?: string;
      event_types?: string[];
      published?: boolean;
      qa_status?: 'checked' | 'pending';
      sort_by?: 'created_at' | 'qa_check';
      sort_order?: 'asc' | 'desc';
    }>) => {
      state.listCache = {
        data: {
          diffs: action.payload.diffs,
          totalCount: action.payload.totalCount,
          currentPage: action.payload.currentPage,
          hasNextPage: action.payload.hasNextPage,
          hasPrevPage: action.payload.hasPrevPage,
        },
        lastFetched: Date.now(),
        periodFilter: action.payload.periodFilter,
        slug: action.payload.slug,
        event_types: action.payload.event_types,
        published: action.payload.published,
        qa_status: action.payload.qa_status,
        sort_by: action.payload.sort_by,
        sort_order: action.payload.sort_order,
      };
    },

    // Append diffs to existing list (for pagination)
    appendExternalDiffs: (state, action: PayloadAction<{
      diffs: Diff[];
      hasNextPage: boolean;
    }>) => {
      if (state.listCache) {
        state.listCache.data.diffs = [
          ...state.listCache.data.diffs,
          ...action.payload.diffs,
        ];
        state.listCache.data.currentPage += 1;
        state.listCache.data.hasNextPage = action.payload.hasNextPage;
      }
    },

    // Set statistics
    setExternalListStatistics: (state, action: PayloadAction<{
      statistics: Statistics;
      periodFilter: string;
      slug?: string;
    }>) => {
      state.listStatistics = {
        data: action.payload.statistics,
        lastFetched: Date.now(),
        periodFilter: action.payload.periodFilter,
        slug: action.payload.slug,
      };
    },

    // Company logos management
    setCompanyLogo: (state, action: PayloadAction<{
      slug: string;
      logoUrl: string;
    }>) => {
      state.companyLogos[action.payload.slug] = action.payload.logoUrl;
    },

    setCompanyLogos: (state, action: PayloadAction<Record<string, string>>) => {
      state.companyLogos = {
        ...state.companyLogos,
        ...action.payload,
      };
    },

    // Request tracking
    addRequestInFlight: (state, action: PayloadAction<string>) => {
      if (!state.requestsInFlight.includes(action.payload)) {
        state.requestsInFlight.push(action.payload);
      }
    },

    removeRequestInFlight: (state, action: PayloadAction<string>) => {
      state.requestsInFlight = state.requestsInFlight.filter(
        key => key !== action.payload
      );
    },

    // Cost tracking
    incrementApiRequestCount: (state, action: PayloadAction<{
      endpoint: string;
      params: Record<string, any>;
    }>) => {
      state.apiRequestCount += 1;
      state.requestLog.push({
        timestamp: Date.now(),
        endpoint: action.payload.endpoint,
        params: action.payload.params,
      });

      // Keep only last 100 requests in log
      if (state.requestLog.length > 100) {
        state.requestLog = state.requestLog.slice(-100);
      }
    },

    resetApiRequestCount: (state) => {
      state.apiRequestCount = 0;
      state.requestLog = [];
    },

    // Navigation list for lightbox
    setExternalNavigationList: (state, action: PayloadAction<string[]>) => {
      state.navigationList = action.payload;
    },

    clearExternalNavigationList: (state) => {
      state.navigationList = null;
    },

    // Filters
    setExternalDiffsFilters: (state, action: PayloadAction<ExternalDiffsState['currentFilters']>) => {
      state.currentFilters = action.payload;
    },

    // Update single diff in list
    updateExternalDiffInList: (state, action: PayloadAction<Diff>) => {
      if (state.listCache) {
        const index = state.listCache.data.diffs.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.listCache.data.diffs[index] = action.payload;
        }
      }
    },

    // Clear cache
    clearExternalDiffsCache: (state) => {
      state.listCache = null;
      state.listStatistics = null;
      // Keep navigation list, company logos, and request tracking
    },

    // Clear everything
    clearExternalDiffsList: (state) => {
      state.listCache = null;
      state.listStatistics = null;
      state.navigationList = null;
      state.currentFilters = {};
      // Keep company logos and request tracking
    },

    // Complete reset (including cost tracking)
    resetExternalDiffsState: () => initialState,
  },
});

export const {
  setExternalDiffsList,
  appendExternalDiffs,
  setExternalListStatistics,
  setCompanyLogo,
  setCompanyLogos,
  addRequestInFlight,
  removeRequestInFlight,
  incrementApiRequestCount,
  resetApiRequestCount,
  setExternalNavigationList,
  clearExternalNavigationList,
  setExternalDiffsFilters,
  updateExternalDiffInList,
  clearExternalDiffsCache,
  clearExternalDiffsList,
  resetExternalDiffsState,
} = externalDiffsSlice.actions;

export default externalDiffsSlice.reducer;

// Selectors

/**
 * Check if cache is valid for given filters
 */
export function isCacheValid(
  cache: CachedData<any> | null,
  periodFilter: string,
  slug?: string,
  eventTypes?: string[],
  published?: boolean,
  qaStatus?: 'checked' | 'pending',
  sortBy?: 'created_at' | 'qa_check',
  sortOrder?: 'asc' | 'desc'
): boolean {
  if (!cache) return false;

  const now = Date.now();
  const isExpired = now - cache.lastFetched > CACHE_TTL;
  if (isExpired) return false;

  const periodMatches = cache.periodFilter === periodFilter;
  const slugMatches = cache.slug === slug;

  // Check if event types match (order-independent comparison)
  const cachedTypes = cache.event_types || [];
  const requestTypes = eventTypes || [];
  const eventTypesMatch =
    JSON.stringify([...cachedTypes].sort()) === JSON.stringify([...requestTypes].sort());

  // Check published, QA status and sort params
  const publishedMatches = cache.published === published;
  const qaStatusMatches = cache.qa_status === qaStatus;
  const sortByMatches = cache.sort_by === sortBy;
  const sortOrderMatches = cache.sort_order === sortOrder;

  return periodMatches && slugMatches && eventTypesMatch && publishedMatches && qaStatusMatches && sortByMatches && sortOrderMatches;
}

/**
 * Generate request key for deduplication
 */
export function generateRequestKey(params: {
  slug?: string;
  period?: string;
  offset?: number;
  event_types?: string[];
}): string {
  // Sort event_types for consistent keys
  const normalized = {
    ...params,
    event_types: params.event_types?.sort(),
  };
  return JSON.stringify(normalized);
}
